import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import './AImpostazioni.css';
import API from '../../api/API';
import EdizioneService from '../../api/edizioneService';
import { BASE_URL } from '../../api/ApiBase';

// "2026-06-06T10:30:00" -> valore per <input type="datetime-local">
const toInputDateTime = (value) => (value ? value.slice(0, 16) : '');
// "2026-06-06T00:00:00" -> valore per <input type="date">
const toInputDate = (value) => (value ? value.slice(0, 10) : '');
// la locandina può essere un data-URL o base64 grezzo (dati storici)
const toImageSrc = (value) => (value.startsWith('data:') ? value : `data:image/jpeg;base64,${value}`);

// categorie: stessi valori DB usati da createEdizioneModal
const CATEGORIE = [
  { label: 'LC', value: 'lc' },
  { label: 'EG', value: 'eg' },
  { label: 'RS', value: 'rs' },
  { label: 'CoCa', value: 'coca' },
  { label: 'RS+CoCa', value: 'rsc' },
];
const CATEGORIA_VALUES = CATEGORIE.map((c) => c.value);

const AImpostazioni = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [edizione, setEdizione] = useState(null);   // ResTorneoDettaglio dal backend
  const [form, setForm] = useState(null);           // campi editabili (dati edizione + iscrizioni)
  const [album, setAlbum] = useState(null);         // FotoAlbum dell'edizione (o null)
  const [albumForm, setAlbumForm] = useState(null);
  const [fotoApprovate, setFotoApprovate] = useState([]);
  const [squadreCount, setSquadreCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [savingSection, setSavingSection] = useState(null); // 'dati' | 'iscrizioni' | 'foto' | 'delete'
  const locandinaInputRef = useRef(null);

  // La chiave e l'evento stanno solo in EdizioneService: chi tocca localStorage a mano lascia
  // il menu del torneo acceso su un'edizione che le pagine non vedono piu'.
  const idEdizione = EdizioneService.get()?.id ?? null;

  const loadAlbum = useCallback(async (id) => {
    const albums = await API.foto.edizione(id);
    if (albums.isSuccess && albums.data && albums.data.length > 0) {
      const a = albums.data[0];
      setAlbum(a);
      setAlbumForm({
        uploadFrom: toInputDateTime(a.uploadFrom),
        uploadTo: toInputDateTime(a.uploadTo),
        voteFrom: toInputDateTime(a.voteFrom),
        voteTo: toInputDateTime(a.voteTo),
        copertina: a.copertina || null,
      });
      const approved = await API.foto.approved(a.id);
      setFotoApprovate(approved.isSuccess && approved.data ? approved.data : []);
    } else {
      setAlbum(null);
      setAlbumForm(null);
      setFotoApprovate([]);
    }
  }, []);

  useEffect(() => {
    if (!idEdizione) {
      setLoading(false);
      return;
    }
    const loadAll = async () => {
      setLoading(true);
      try {
        const dettaglio = await API.torneo.dettaglio(idEdizione);
        if (dettaglio.isSuccess && dettaglio.data) {
          const d = dettaglio.data;
          setEdizione(d);
          // normalizza la categoria sui valori noti (i dati storici hanno anche maiuscole/varianti)
          const rawCategoria = (d.categoria || '').toLowerCase();
          setForm({
            edizione: d.edizione || '',
            luogo: d.luogo || '',
            categoria: CATEGORIA_VALUES.includes(rawCategoria) ? rawCategoria : (d.categoria || ''),
            pubblico: d.pubblico !== false,
            locandina: d.locandina || '',
            dataInizio: toInputDate(d.dataInizio),
            dataFine: toInputDate(d.dataFine),
            chiusuraIscrizioni: toInputDateTime(d.chiusuraIscrizioni),
            chiusuraModifiche: toInputDateTime(d.chiusuraModifiche),
          });
        } else {
          enqueueSnackbar("Errore nel caricamento dell'edizione", { variant: 'error' });
        }

        const squadre = await API.torneo.squadre(idEdizione);
        setSquadreCount(squadre.isSuccess && squadre.data ? squadre.data.length : 0);

        await loadAlbum(idEdizione);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [idEdizione, loadAlbum, enqueueSnackbar]);

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAlbumChange = (field) => (e) => {
    setAlbumForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Upload locandina: stesse regole di createEdizioneModal (jpg/png/gif/webp, max 2MB, base64)
  const handleLocandinaChange = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // permette di riselezionare lo stesso file
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      enqueueSnackbar('Formato non supportato. Usa JPG, PNG, GIF o WebP.', { variant: 'error' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar("L'immagine non deve superare i 2MB.", { variant: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setForm((prev) => ({ ...prev, locandina: reader.result }));
    reader.onerror = () => enqueueSnackbar('Errore durante la lettura del file.', { variant: 'error' });
    reader.readAsDataURL(file);
  };

  // Salva i dati dell'edizione (condiviso da "Dati edizione" e "Iscrizioni")
  const saveTorneo = async (section) => {
    if (!form.dataInizio || !form.dataFine || !form.chiusuraIscrizioni || !form.chiusuraModifiche) {
      enqueueSnackbar('Tutte le date sono obbligatorie', { variant: 'error' });
      return;
    }
    if (new Date(form.dataFine) < new Date(form.dataInizio)) {
      enqueueSnackbar('La data di fine non può precedere quella di inizio', { variant: 'error' });
      return;
    }
    if (!form.edizione.trim() || !form.luogo.trim() || !form.categoria.trim()) {
      enqueueSnackbar('Nome edizione, luogo e categoria sono obbligatori', { variant: 'error' });
      return;
    }
    if (form.chiusuraIscrizioni && form.chiusuraModifiche &&
        new Date(form.chiusuraModifiche) < new Date(form.chiusuraIscrizioni)) {
      enqueueSnackbar('Attenzione: la chiusura modifiche precede la chiusura iscrizioni', { variant: 'warning' });
    }

    setSavingSection(section);
    try {
      // payload nel formato ReqTorneo: `torneo` è il nome del gruppo (campo obbligatorio nel binding)
      const payload = {
        id: edizione.id,
        torneo: edizione.torneo,
        edizione: form.edizione,
        luogo: form.luogo,
        categoria: form.categoria,
        censiti: edizione.censiti,
        dataInizio: form.dataInizio,
        dataFine: form.dataFine,
        chiusuraIscrizioni: form.chiusuraIscrizioni,
        chiusuraModifiche: form.chiusuraModifiche,
        avvio: edizione.avvio,
        chiusura: edizione.chiusura,
        quota: edizione.quota,
        caparra: edizione.caparra || 0,
        cibo: edizione.cibo,
        preiscrizione: edizione.preiscrizione,
        pubblico: form.pubblico,
        locandina: form.locandina || null,
      };
      const response = await API.torneo.update(payload);
      if (response.isSuccess) {
        enqueueSnackbar('Impostazioni salvate', { variant: 'success' });
        // mantiene allineata la data mostrata nei bottoni edizione di /admin/tornei
        EdizioneService.set({ ...(EdizioneService.get() ?? {}), date: form.dataInizio });
      } else {
        enqueueSnackbar(`Errore nel salvataggio: ${response.data || response.errorMsg || ''}`, { variant: 'error' });
      }
    } finally {
      setSavingSection(null);
    }
  };

  const createAlbum = async () => {
    setSavingSection('foto');
    try {
      const response = await API.foto.createAlbum(idEdizione);
      if (response.isSuccess) {
        enqueueSnackbar('Album creato', { variant: 'success' });
        await loadAlbum(idEdizione);
      } else {
        enqueueSnackbar(`Errore nella creazione dell'album: ${response.data || ''}`, { variant: 'error' });
      }
    } finally {
      setSavingSection(null);
    }
  };

  const saveAlbum = async () => {
    setSavingSection('foto');
    try {
      const payload = {
        id: album.id,
        torneo: album.torneo,
        uploadFrom: albumForm.uploadFrom || null,
        uploadTo: albumForm.uploadTo || null,
        voteFrom: albumForm.voteFrom || null,
        voteTo: albumForm.voteTo || null,
        copertina: albumForm.copertina || null,
        overlay: album.overlay || null,
      };
      const response = await API.foto.updateAlbum(payload);
      if (response.isSuccess) {
        enqueueSnackbar('Impostazioni foto salvate', { variant: 'success' });
      } else {
        enqueueSnackbar(`Errore nel salvataggio: ${response.data || ''}`, { variant: 'error' });
      }
    } finally {
      setSavingSection(null);
    }
  };

  const deleteEdizione = async () => {
    setSavingSection('delete');
    try {
      const response = await API.torneo.delete(edizione.id);
      if (response.isSuccess) {
        enqueueSnackbar('Edizione eliminata', { variant: 'success' });
        EdizioneService.clear();
        navigate('/admin/tornei');
      } else {
        // 409 (squadre iscritte) o altro errore: il backend risponde col messaggio
        enqueueSnackbar(response.data || "Errore durante l'eliminazione", { variant: 'error' });
        setShowDeleteModal(false);
      }
    } finally {
      setSavingSection(null);
    }
  };

  if (loading) {
    return (
      <div className="aimpostazioni-container">
        <div className="aimpostazioni-loading">
          <div className="aimpostazioni-spinner"></div>
          <p>Caricamento impostazioni...</p>
        </div>
      </div>
    );
  }

  if (!idEdizione || !edizione || !form) {
    return (
      <div className="aimpostazioni-container">
        <div className="aimpostazioni-no-edition">
          <h2>Nessuna edizione selezionata</h2>
          <p>Seleziona un'edizione per gestirne le impostazioni.</p>
        </div>
      </div>
    );
  }

  const canDelete = squadreCount === 0;
  // dati edizione e iscrizioni si modificano solo a torneo in preparazione; la sezione foto resta sempre attiva
  const inPreparazione = edizione.stato === 'preparazione';

  return (
    <div className="aimpostazioni-container">
      <div className="aimpostazioni-header">
        <h1>Impostazioni</h1>
        <p className="aimpostazioni-subtitle">
          {edizione.torneo} — {edizione.edizione}
        </p>
      </div>

      {!inPreparazione && (
        <div className="aimpostazioni-banner">
          <i className="fas fa-lock"></i>
          {edizione.stato === 'inCorso' ? 'Torneo in corso' : 'Torneo chiuso'}: dati edizione e
          iscrizioni sono in sola lettura. La gestione foto resta disponibile.
        </div>
      )}

      <div className="aimpostazioni-columns">
      <div className="aimpostazioni-column">

      {/* Sezione: Dati edizione */}
      <div className="aimpostazioni-card">
        <h2><i className="fas fa-trophy"></i> Dati edizione</h2>
        <fieldset
          className="aimpostazioni-fieldset"
          disabled={!inPreparazione}
          title={!inPreparazione ? 'Modificabile solo a torneo in preparazione' : undefined}
        >
        <div className="aimpostazioni-form-grid">
          <label>
            Nome edizione
            {/* la colonna edizione è varchar(10) sul DB */}
            <input type="text" maxLength={10} value={form.edizione} onChange={handleFormChange('edizione')} />
          </label>
          <label>
            Luogo
            <input type="text" value={form.luogo} onChange={handleFormChange('luogo')} />
          </label>
          <div className="aimpostazioni-field">
            <span>Visibilità</span>
            <label
              className="aimpostazioni-toggle"
              title="Se disattivato, l'edizione non compare nelle pagine pubbliche (statistiche, foto, iscrizioni aperte). L'accesso da link diretto resta possibile."
            >
              <input
                type="checkbox"
                checked={form.pubblico}
                onChange={(e) => setForm((prev) => ({ ...prev, pubblico: e.target.checked }))}
              />
              Pubblico
              <i className="fas fa-info-circle"></i>
            </label>
          </div>
          <div className="aimpostazioni-field">
            <span>Categoria</span>
            <div className="aimpostazioni-radio-group">
              {CATEGORIE.map((c) => (
                <label key={c.value} className="aimpostazioni-radio">
                  <input
                    type="radio"
                    name="categoria"
                    value={c.value}
                    checked={form.categoria === c.value}
                    onChange={handleFormChange('categoria')}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div className="aimpostazioni-field">
            <span>Locandina</span>
            <div className="aimpostazioni-locandina">
              {form.locandina ? (
                <>
                  <img src={toImageSrc(form.locandina)} alt="Locandina edizione" />
                  <div className="aimpostazioni-locandina-actions">
                    <button
                      type="button"
                      className="aimpostazioni-btn aimpostazioni-btn-secondary"
                      onClick={() => locandinaInputRef.current?.click()}
                    >
                      <i className="fas fa-exchange-alt"></i>
                      Cambia
                    </button>
                    <button
                      type="button"
                      className="aimpostazioni-btn aimpostazioni-btn-secondary"
                      onClick={() => setForm((prev) => ({ ...prev, locandina: '' }))}
                    >
                      <i className="fas fa-trash"></i>
                      Rimuovi
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  className="aimpostazioni-locandina-empty"
                  onClick={() => locandinaInputRef.current?.click()}
                >
                  <i className="fas fa-image"></i>
                  <span>Carica locandina</span>
                  <small>JPG, PNG, GIF o WebP — max 2MB</small>
                </button>
              )}
              <input
                ref={locandinaInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleLocandinaChange}
              />
            </div>
          </div>
          <div className="aimpostazioni-field-stack">
            <label>
              Data inizio
              <input type="date" value={form.dataInizio} onChange={handleFormChange('dataInizio')} />
            </label>
            <label>
              Data fine
              <input type="date" value={form.dataFine} onChange={handleFormChange('dataFine')} />
            </label>
          </div>
        </div>
        <div className="aimpostazioni-card-actions">
          <button
            className="aimpostazioni-btn aimpostazioni-btn-primary"
            onClick={() => saveTorneo('dati')}
            disabled={savingSection === 'dati'}
          >
            <i className="fas fa-save"></i>
            {savingSection === 'dati' ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
        </fieldset>
      </div>

      {/* Sezione: Zona pericolosa — visibile solo se l'eliminazione è possibile (nessuna squadra iscritta) */}
      {canDelete && (
        <div className="aimpostazioni-card aimpostazioni-danger">
          <h2><i className="fas fa-exclamation-triangle"></i> Zona pericolosa</h2>
          <p>L'eliminazione dell'edizione è definitiva e rimuove anche formula, planning e album foto.</p>
          <button
            className="aimpostazioni-btn aimpostazioni-btn-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={savingSection === 'delete'}
          >
            <i className="fas fa-trash"></i>
            Elimina edizione
          </button>
        </div>
      )}

      </div>
      <div className="aimpostazioni-column">

      {/* Sezione: Iscrizioni */}
      <div className="aimpostazioni-card">
        <h2><i className="fas fa-clipboard-list"></i> Iscrizioni</h2>
        <fieldset
          className="aimpostazioni-fieldset"
          disabled={!inPreparazione}
          title={!inPreparazione ? 'Modificabile solo a torneo in preparazione' : undefined}
        >
        <div className="aimpostazioni-form-grid">
          <label>
            Chiusura iscrizioni
            <input
              type="datetime-local"
              value={form.chiusuraIscrizioni}
              onChange={handleFormChange('chiusuraIscrizioni')}
            />
          </label>
          <label>
            Chiusura modifica squadre
            <input
              type="datetime-local"
              value={form.chiusuraModifiche}
              onChange={handleFormChange('chiusuraModifiche')}
            />
          </label>
        </div>
        <div className="aimpostazioni-card-actions">
          <button
            className="aimpostazioni-btn aimpostazioni-btn-primary"
            onClick={() => saveTorneo('iscrizioni')}
            disabled={savingSection === 'iscrizioni'}
          >
            <i className="fas fa-save"></i>
            {savingSection === 'iscrizioni' ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
        </fieldset>
      </div>

      {/* Sezione: Foto */}
      <div className="aimpostazioni-card">
        <h2><i className="fas fa-images"></i> Foto</h2>
        {!album ? (
          <div className="aimpostazioni-empty-album">
            <p>Nessun album foto per questa edizione.</p>
            <button
              className="aimpostazioni-btn aimpostazioni-btn-primary"
              onClick={createAlbum}
              disabled={savingSection === 'foto'}
            >
              <i className="fas fa-plus"></i>
              {savingSection === 'foto' ? 'Creazione...' : 'Crea album'}
            </button>
          </div>
        ) : (
          <>
            <div className="aimpostazioni-form-grid">
              <label>
                Caricamento foto dal
                <input type="datetime-local" value={albumForm.uploadFrom} onChange={handleAlbumChange('uploadFrom')} />
              </label>
              <label>
                Caricamento foto al
                <input type="datetime-local" value={albumForm.uploadTo} onChange={handleAlbumChange('uploadTo')} />
              </label>
              <label>
                Votazione dal
                <input type="datetime-local" value={albumForm.voteFrom} onChange={handleAlbumChange('voteFrom')} />
              </label>
              <label>
                Votazione al
                <input type="datetime-local" value={albumForm.voteTo} onChange={handleAlbumChange('voteTo')} />
              </label>
            </div>

            <p className="aimpostazioni-hint">
              Caricamento e votazione sono consentiti solo nei periodi impostati: senza date restano disabilitati.
            </p>

            <h3>Copertina</h3>
            {fotoApprovate.length === 0 ? (
              <p className="aimpostazioni-hint">
                Nessuna foto approvata disponibile: la copertina si sceglie tra le foto approvate dell'album.
              </p>
            ) : (
              <div className="aimpostazioni-copertina-grid">
                {fotoApprovate.map((foto) => (
                  <button
                    key={foto.id}
                    type="button"
                    className={`aimpostazioni-copertina-item ${albumForm.copertina === foto.nome ? 'selected' : ''}`}
                    onClick={() =>
                      setAlbumForm((prev) => ({
                        ...prev,
                        copertina: prev.copertina === foto.nome ? null : foto.nome,
                      }))
                    }
                    title={albumForm.copertina === foto.nome ? 'Rimuovi copertina' : 'Imposta come copertina'}
                  >
                    <img
                      src={`${BASE_URL}/foto/thumbnail/${foto.album}/${foto.nome}`}
                      alt={foto.nome}
                      loading="lazy"
                    />
                    {albumForm.copertina === foto.nome && (
                      <span className="aimpostazioni-copertina-badge">
                        <i className="fas fa-check"></i> Copertina
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="aimpostazioni-card-actions">
              <button
                className="aimpostazioni-btn aimpostazioni-btn-primary"
                onClick={saveAlbum}
                disabled={savingSection === 'foto'}
              >
                <i className="fas fa-save"></i>
                {savingSection === 'foto' ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </>
        )}
      </div>

      </div>
      </div>

      {/* Modale di conferma eliminazione */}
      {showDeleteModal && (
        <div className="aimpostazioni-modal-overlay">
          <div className="aimpostazioni-modal">
            <h3>Conferma eliminazione</h3>
            <p>
              Sei sicuro di voler eliminare l'edizione <strong>{edizione.edizione}</strong> di{' '}
              <strong>{edizione.torneo}</strong>? L'operazione non è reversibile.
            </p>
            <div className="aimpostazioni-modal-actions">
              <button
                className="aimpostazioni-btn aimpostazioni-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Annulla
              </button>
              <button
                className="aimpostazioni-btn aimpostazioni-btn-danger"
                onClick={deleteEdizione}
                disabled={savingSection === 'delete'}
              >
                {savingSection === 'delete' ? 'Eliminazione...' : 'Elimina definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AImpostazioni;
